import React, { useEffect, useState } from "react";
import { Card } from "@blueprintjs/core";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { DefaultOption } from "../../../../translations/experiments/Default";
import { colors, commonStyles } from "../../../../components/colors";

const barColors = [colors.lichtBlau, colors.darkGreen]; // For group bar charts
const barColorsContrast = [colors.lichtBlau, colors.sienna]; // For overall percentage bar

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 350,
    maxWidth: 400,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const summaryMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 800,
    marginLeft: "auto",
    marginRight: "auto",
    padding: "16px 24px"
};

const overallCardStyle: React.CSSProperties = {
    background: colors.white,
    maxWidth: 800,
    width: "100%",
    margin: "32px auto 0 auto",
    padding: "28px 32px",
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
};

function getCompGroupFromDefaultOption(compDefault: number) {
    if (compDefault === 1) return 1; // Nicht kompensieren vorausgewählt
    if (compDefault === 0) return 2; // Kompensieren vorausgewählt
    return null;
}

function getUserCompSelection(userData: any) {
    if (userData && userData.CrossoverDefault) {
        let found: any[] = [];
        for (const entry of Object.values(userData.CrossoverDefault)) {
            if (
                entry &&
                typeof entry === "object" &&
                Array.isArray((entry as any).selections)
            ) {
                for (const selection of (entry as any).selections) {
                    if (
                        selection &&
                        (selection.defaultOption === 0 || selection.defaultOption === 1) &&
                        (selection.choice === 0 || selection.choice === 1)
                    ) {
                        found.push(selection);
                    }
                }
            }
        }
        return found.length > 1 ? found[1] : found[0];
    }
    return null;
}

function getUserCompSummary(selection: any, t: any) {
    if (!selection) return {};
    const groupNum = getCompGroupFromDefaultOption(selection.defaultOption);
    const voreinstellung =
        selection.defaultOption === 1
            ? t.compensationRejectShort
            : t.compensationAcceptShort;

    // Bold markups for replacements
    const groupNumBold = `<b>${groupNum}</b>`;
    const voreinstellungBold = `<b>${voreinstellung}</b>`;

    // "Entscheidung": Für oder gegen Default
    const isDefaultChosen = selection.choice === selection.defaultOption;

    let alignmentText = "";
    if (isDefaultChosen) {
        alignmentText = t.shortSummaryDefault
            .replace("{group}", groupNumBold)
            .replace("{defaultOption}", voreinstellungBold);
    } else {
        alignmentText = t.shortSummaryAgainst
            .replace("{group}", groupNumBold)
            .replace("{defaultOption}", voreinstellungBold);
    }

    return { groupNum, voreinstellung, alignmentText };
}

export const ResultScreenCrossoverCompensationComponent: React.FC = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    // FIX: Access results at root, not under screens
    const t = {
        ...DefaultOption[language].results.compensation,
        compensationAccept: DefaultOption[language].screens.screen3.compensationBanner.options[0].label,
        compensationReject: DefaultOption[language].screens.screen3.compensationBanner.options[1].label,
        compensationAcceptShort: language === "de" ? "Kompensation" : "Compensation",
        compensationRejectShort: language === "de" ? "Keine Kompensation" : "No compensation",
        defaultChosen: DefaultOption[language].results.compensation.defaultChosen,
        againstDefault: DefaultOption[language].results.compensation.againstDefault,
        // New: Short summary templates (de/en), now with <b> markup for group and option
        shortSummaryDefault:
            language === "de"
                ? 'Du warst zufällig in Gruppe {group}, wo die Option {defaultOption} vorausgewählt war. Du hast dich für die <b>Voreinstellung</b> entschieden.'
                : 'You were randomly assigned to group {group}, where the option {defaultOption} was preselected. You chose the <b>preset option</b>.',
        shortSummaryAgainst:
            language === "de"
                ? 'Du warst zufällig in Gruppe {group}, wo die Option {defaultOption} vorausgewählt war. Du hast dich <b>gegen die Voreinstellung</b> entschieden.'
                : 'You were randomly assigned to group {group}, where the option {defaultOption} was preselected. You decided <b>against the preset option</b>.'
    };

    const [group1Counts, setGroup1Counts] = useState([0, 0]);
    const [group2Counts, setGroup2Counts] = useState([0, 0]);
    const [totalAligned, setTotalAligned] = useState(0);
    const [totalMisaligned, setTotalMisaligned] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const userCompSelection = getUserCompSelection(userData);
    const summary = getUserCompSummary(userCompSelection, t);

    useEffect(() => {
        setLoading(true);
        getGroupData().then((data: any[]) => {
            let grp1Comp = 0, grp1NoComp = 0, grp2Comp = 0, grp2NoComp = 0;
            let aligned = 0, misaligned = 0;
            let sum = 0;

            data?.forEach((dat: any) => {
                const selection = getUserCompSelection(dat);
                if (selection) {
                    sum++;
                    const group = getCompGroupFromDefaultOption(selection.defaultOption);
                    if (selection.choice === 0) {
                        group === 1 ? grp1Comp++ : grp2Comp++;
                    } else if (selection.choice === 1) {
                        group === 1 ? grp1NoComp++ : grp2NoComp++;
                    }
                    if (selection.choice === selection.defaultOption) {
                        aligned++;
                    } else {
                        misaligned++;
                    }
                }
            });

            setGroup1Counts([grp1Comp, grp1NoComp]);
            setGroup2Counts([grp2Comp, grp2NoComp]);
            setTotalAligned(aligned);
            setTotalMisaligned(misaligned);
            setTotal(sum);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {t.loading}
        </div>;
    }

    const alignedPercent = total > 0 ? Math.round((totalAligned / total) * 100) : 0;
    const misalignedPercent = total > 0 ? Math.round((totalMisaligned / total) * 100) : 0;
    const relativeData = [
        { name: t.defaultChosen, value: alignedPercent },
        { name: t.againstDefault, value: misalignedPercent }
    ];

    const highlightStyle = {
        border: `2px solid ${colors.brillantBlue}`,
    };

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t.title}
            </h2>
            <div style={{
                display: "flex",
                gap: 24,
                marginTop: 32,
                flexWrap: "wrap",
                justifyContent: "center"
            }}>
                <Card
                    style={{
                        ...cardStyle,
                        ...(summary.groupNum === 1 ? highlightStyle : {})
                    }}
                >
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>{t.group1.title}</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                            data={[
                                { name: t.compensationAcceptShort, value: group1Counts[0] },
                                { name: t.compensationRejectShort, value: group1Counts[1] }
                            ]}
                            barCategoryGap={40}
                        >
                            <XAxis dataKey="name" interval={0} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="value" barSize={36}>
                                <Cell fill={barColors[0]} />
                                <Cell fill={barColors[1]} />
                                <LabelList dataKey="value" position="top" />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
                <Card
                    style={{
                        ...cardStyle,
                        ...(summary.groupNum === 2 ? highlightStyle : {})
                    }}
                >
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>{t.group2.title}</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart
                            data={[
                                { name: t.compensationAcceptShort, value: group2Counts[0] },
                                { name: t.compensationRejectShort, value: group2Counts[1] }
                            ]}
                            barCategoryGap={40}
                        >
                            <XAxis dataKey="name" interval={0} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="value" barSize={36}>
                                <Cell fill={barColors[0]} />
                                <Cell fill={barColors[1]} />
                                <LabelList dataKey="value" position="top" />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
            <div style={summaryMessageStyle}>
                {summary.groupNum && (
                    <span
                        dangerouslySetInnerHTML={{
                            __html: summary.alignmentText
                        }}
                    />
                )}
            </div>
            <div style={{
                marginTop: 32,
                textAlign: "center",
                fontSize: 18,
                maxWidth: 800,
                width: "100%",
                marginLeft: "auto",
                marginRight: "auto"
            }}>
                <Card style={overallCardStyle}>
                    <h4 style={{
                        marginBottom: 24,
                        color: colors.brillantBlue,
                        fontWeight: 600,
                        fontSize: 20
                    }}>
                        {t.overall}
                    </h4>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart
                            data={relativeData}
                            barCategoryGap={60}
                        >
                            <XAxis
                                dataKey="name"
                                interval={0}
                                angle={0}
                                textAnchor="middle"
                                tick={{ fontSize: 16 }}
                            />
                            <YAxis
                                domain={[0, 100]}
                                allowDecimals={false}
                                tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip formatter={(value: any) => `${value}%`} />
                            <Bar dataKey="value" barSize={56}>
                                <Cell fill={barColorsContrast[0]} />
                                <Cell fill={barColorsContrast[1]} />
                                <LabelList dataKey="value" position="top" formatter={(value: any) => `${value}%`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </div>
    );
};

export const ResultScreenCrossoverCompensation: ScreenConfig = {
    component: ResultScreenCrossoverCompensationComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};